/** Copyright 2023 Midas Healthcare Solutions - All Rights Reserved **/
import { Injectable } from '@angular/core';
import {
  ApiService,
  EOverviewActionGroupingType,
  EOverViewPercentFullLimits,
  IOverviewAction,
  IOverviewActionsFilter,
} from '@midas/shared/common';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { fetch } from '@nrwl/angular';
import { map } from 'rxjs';
import * as OverviewActionActions from './overview-actions.actions';

@Injectable()
export class OverviewActionsEffects {
  readonly queryOverviewActions$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(OverviewActionActions.queryOverviewActions),
      fetch({
        run: () => {
          return this.api.get<{ carts: IOverviewAction[] }>('/overview/actions/').pipe(
            map((res) => {
              return OverviewActionActions.queryOverviewActionsSuccess({
                carts: res.carts,
              });
            }),
          );
        },
        onError: (action, error) => {
          return OverviewActionActions.queryOverviewActionsError({ error });
        },
      }),
    );
  });

  readonly queryOverviewActionBySearchValue$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(OverviewActionActions.queryOverviewActionsBySearchValue),
      fetch({
        run: (searchValue) => {
          return this.api.get<{ carts: IOverviewAction[] }>('/overview/actions/').pipe(
            map((res) => {
              const filteredData = this.fnFilterActions(searchValue, res.carts);
              return OverviewActionActions.queryOverviewactionsBySearchValueSuccess({ carts: filteredData });
            }),
          );
        },
        onError: (action, error) => {
          return OverviewActionActions.queryOverviewactionsBySearchValueError({ error });
        },
      }),
    );
  });

  fnFilterActions = (filter: IOverviewActionsFilter, data: IOverviewAction[]) => {
    const { locationIds, statusIds, searchValue } = filter;

    if (locationIds.length) {
      data = data.filter((view) => {
        return locationIds.length > 0 && locationIds.indexOf(view.locationId) >= 0;
      });
    }

    if (searchValue !== '') {
      data = this.fnFilterBySearchValue(searchValue, data);
    }

    if (statusIds.length) {
      data = data.filter((view) => this.fnFilterByStatus(view, statusIds));
    }

    return data;
  };

  fnFilterBySearchValue = (searchValue: string, data: IOverviewAction[]): IOverviewAction[] => {
    return data.filter((view) => {
      return (
        view.name.toLowerCase().includes(searchValue.toLowerCase()) ||
        view.locationName.toLowerCase().includes(searchValue.toLowerCase()) ||
        view.organizationName.toLowerCase().includes(searchValue.toLowerCase())
      );
    });
  };

  fnFilterByStatus = (item: IOverviewAction, statusFilter: string[]) => {
    const isError = (item: IOverviewAction) => item.error;

    const isFull = (item: IOverviewAction) =>
      item.collectionBins.some((bin) => bin.percentFull === EOverViewPercentFullLimits.full);

    const isLockboxFull = (item: IOverviewAction) =>
      item.collectionBox && item.collectionBox.percentFull === EOverViewPercentFullLimits.full;

    const isReadyForReview = (item: IOverviewAction) => item.readyForReview;

    return statusFilter.some((status) => {
      switch (status) {
        case EOverviewActionGroupingType.ERROR:
          return isError(item);
        case EOverviewActionGroupingType.NOTIFY_BUILDING_SERVICE:
          return isFull(item);
        case EOverviewActionGroupingType.LOCKBOXFULL:
          return isLockboxFull(item);
        case EOverviewActionGroupingType.READYFORREVIEW:
          return isReadyForReview(item);
        default:
          return false;
      }
    });
  };

  constructor(private actions$: Actions, private api: ApiService) {}
}
